import { StarRounded } from '@mui/icons-material';
import { Box, LinearProgress, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';

import { useI18n } from '../../../../../application/language/useI18n';
import { IUserDataModel, UserDataLevel } from '../../../../../service/userData/IUserDataModel';
import { Colors } from '../../../../design/color/Colors';

export interface IUserInfoCard {
  fio: string;
  role: string | null;
  userData: IUserDataModel;
}

const useStyles = makeStyles(() => ({
  flexBetween: { display: 'flex', justifyContent: 'space-between' },
  flexBetweenMb: { display: 'flex', justifyContent: 'space-between', marginBottom: '10px' },
  flexGap6Center: { display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' },
  flexGap8: { display: 'flex', gap: '8px' },
  levelCol: { display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' },
  levelBar: { display: 'flex', justifyContent: 'space-between', width: '100%' },
  progress: { width: '117px' },
  text14: { fontSize: '14px' },
  star: {
    width: '12px',
    height: '12px',
    background: Colors.primary,
    borderRadius: '50%',
    color: Colors.bgElevated,
  },
}));

export const UserInfoCard = observer(
  ({ userData: { toNextLevelScore, level, totalScore, maxScore, levelThresholds } }: IUserInfoCard) => {
    const i18n = useI18n();
    const classes = useStyles();
    const [nextLevelScore, setNextLevelScore] = useState<string>('');

    useEffect(() => {
      if (totalScore === 0) {
        setNextLevelScore(i18n.translate('userCardInfo.learnAndLevelUp'));
      } else if (toNextLevelScore === 0) {
        setNextLevelScore(
          maxScore === 0
            ? i18n.translate('userCardInfo.learnAndLevelUp')
            : i18n.translate('userCardInfo.allUnitsPassed'),
        );
      } else if (level === UserDataLevel.SENIOR) {
        setNextLevelScore(`${i18n.translate('userCardInfo.toMax')} ${toNextLevelScore}`);
      } else {
        setNextLevelScore(`${i18n.translate('userCardInfo.toNextLevel')} ${toNextLevelScore}`);
      }
    }, [toNextLevelScore, totalScore, maxScore, level]);

    const highlightColor = Colors.primary;
    const inactiveColor = Colors.textSecondary;
    const safeMaxScore = Number(maxScore || 0);
    const thresholds = useMemo(() => levelThresholds || {
      junior: Math.round((safeMaxScore * 35) / 100),
      middle: Math.round((safeMaxScore * 69) / 100),
      senior: safeMaxScore,
    }, [levelThresholds, safeMaxScore]);

    const levels = useMemo(() => {
      const calcProgress = (targetLevel: 'junior' | 'middle' | 'senior') => {
        let progress = 0;
        if (targetLevel === 'junior') {
          progress = level === UserDataLevel.SENIOR || level === UserDataLevel.MIDDLE
            ? 100
            : totalScore === 0 ? 0 : Math.round(totalScore / ((maxScore * 0.35) / 100));
        } else if (targetLevel === 'middle') {
          progress = level === UserDataLevel.SENIOR
            ? 100
            : level === UserDataLevel.MIDDLE
            ? Math.round((totalScore - (maxScore * 35) / 100) / ((maxScore * 0.34) / 100))
            : 0;
        } else {
          progress = level === UserDataLevel.SENIOR
            ? toNextLevelScore === 0 ? 100 : Math.round((totalScore - Math.round((maxScore * 69) / 100 + 1)) / ((maxScore * 0.31) / 100))
            : 0;
        }
        const isFull = Math.round(progress) >= 100;
        const reachedNewLevel =
          (targetLevel === 'middle' && level === UserDataLevel.MIDDLE) ||
          (targetLevel === 'senior' && level === UserDataLevel.SENIOR);
        const isPartial = (progress > 0 && Math.round(progress) < 100) || reachedNewLevel;
        return {
          value: progress,
          rankColor: isFull || isPartial ? highlightColor : inactiveColor,
          scoreColor: isFull ? highlightColor : inactiveColor,
        };
      };
      return {
        junior: calcProgress('junior'),
        middle: calcProgress('middle'),
        senior: calcProgress('senior'),
      };
    }, [level, totalScore, maxScore, toNextLevelScore, highlightColor, inactiveColor]);

    return (
      <Box className={classes.flexBetween}>
        <Box>
          <Box className={classes.flexBetweenMb}>
            <Box className={classes.flexGap6Center}>
              <StarRounded className={classes.star} />
              <Typography className={classes.text14} style={{ color: inactiveColor }}>{totalScore}</Typography>
            </Box>
            <Typography className={classes.text14} style={{ color: inactiveColor }}>{nextLevelScore}</Typography>
          </Box>

          <Box className={classes.flexGap8}>
            {(['junior', 'middle', 'senior'] as const).map((key) => (
              <Box key={key} className={classes.levelCol}>
                <LinearProgress variant="determinate" value={levels[key].value} className={classes.progress} />
                <Box className={classes.levelBar}>
                  <Typography className={classes.text14} style={{ color: levels[key].rankColor, textAlign: 'center' }}>
                    {i18n.translate(`userCardInfo.${key}`)}
                  </Typography>
                  <Typography className={classes.text14} style={{ color: levels[key].scoreColor }}>
                    {thresholds[key]}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  },
);
